import React from 'react';
import styled from 'styled-components';
import { Check, X, Clock, Circle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const STATUS_COLORS = {
  approved: { color: '#16A34A', bg: '#DCFCE7' },
  waiting: { color: '#EA580C', bg: '#FFF7ED' },
  rejected: { color: '#DC2626', bg: '#FEE2E2' },
  pending: { color: '#94A3B8', bg: '#F1F5F9' },
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Item = styled.div`
  display: flex;
  gap: 14px;
  align-items: stretch;
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
`;

const IconCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  border: 2px solid ${({ $color }) => $color};
`;

const Line = styled.div`
  width: 2px;
  flex: 1;
  background: ${({ $color }) => $color || '#E2E8F0'};
  min-height: 24px;
`;

const Content = styled.div`
  padding: 4px 0 24px;
  flex: 1;
`;

const Name = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Type = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #94A3B8;
  margin-top: 2px;
`;

const StatusText = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
  margin-top: 6px;
  color: ${({ $color }) => $color};
`;

const Empty = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #94A3B8;
  padding: 24px;
  text-align: center;
`;

function StepIcon({ status }) {
  const cfg = STATUS_COLORS[status] || STATUS_COLORS.pending;
  const Icon = status === 'approved' ? Check
    : status === 'rejected' ? X
    : status === 'waiting' ? Clock
    : Circle;
  return (
    <IconCircle $color={cfg.color} $bg={cfg.bg}>
      <Icon size={16} />
    </IconCircle>
  );
}

// Pozisyon adımının ikinci satırı. Kod kullanıcıya bir şey söylemez: doluysa
// ünvan (ad zaten üst satırda), boşsa "Boş pozisyon" (üst satırda ünvan durur).
function positionDetail(step, t) {
  if (step.isVacant) return t('approvalFlow.vacantPosition', { defaultValue: 'Boş pozisyon' });
  // Eski sunucu ünvanı ayrı göndermez; ad ünvana düştüyse tekrar yazma.
  if (step.approverTitle && step.approverTitle !== step.approverName) return step.approverTitle;
  return null;
}

/**
 * Vertical approval-flow timeline. Steps come from
 * `/v1/approval-flows/{id}` (sorted by stepOrder). The parent passes the
 * source object (leave/expense) so step status can be derived from it.
 *
 * Status derivation rules:
 * - Object status === 'approved' → all steps approved.
 * - Object status === 'rejected' → step matching rejectedUser is "rejected";
 *   earlier steps "approved"; later steps stay "pending".
 * - Object status === 'waiting' → step matching currentApprover is "waiting";
 *   earlier steps "approved"; later steps "pending".
 */
export default function ApprovalFlowTimeline({ steps, source }) {
  const { t } = useTranslation();

  if (!steps || steps.length === 0) {
    return <Empty>{t('common.noApprovalFlow', { defaultValue: 'Onay akışı bilgisi bulunamadı.' })}</Empty>;
  }

  const getStatus = (step) => {
    if (!source) return 'pending';
    if (source.status === 'approved') return 'approved';
    if (source.status === 'rejected') {
      const rejectedStep = steps.find((s) => s.approverId === source.rejectedUser);
      if (rejectedStep && step.stepOrder < rejectedStep.stepOrder) return 'approved';
      if (rejectedStep && step.stepOrder > rejectedStep.stepOrder) return 'pending';
      if (step.approverId === source.rejectedUser) return 'rejected';
      return 'rejected';
    }
    if (source.status === 'waiting') {
      if (source.currentApprover === step.approverId) return 'waiting';
      const currentStep = steps.find((s) => s.approverId === source.currentApprover);
      if (currentStep && step.stepOrder < currentStep.stepOrder) return 'approved';
      return 'pending';
    }
    return 'pending';
  };

  return (
    <Wrapper>
      {steps.map((step, i) => {
        const status = getStatus(step);
        const cfg = STATUS_COLORS[status] || STATUS_COLORS.pending;
        const isLast = i === steps.length - 1;
        return (
          <Item key={step.id}>
            <Left>
              <StepIcon status={status} />
              {!isLast && <Line $color={status === 'approved' ? STATUS_COLORS.approved.color : '#E2E8F0'} />}
            </Left>
            <Content>
              <Name>{step.approverName || step.approverTitle || step.approverCode || '—'}</Name>
              <Type>
                {step.approverType === 'person'
                  ? t('common.personApprover', { defaultValue: 'Kişi Onayı' })
                  : [
                    positionDetail(step, t),
                    t('common.positionApprover', { defaultValue: 'Pozisyon Onayı' }),
                  ].filter(Boolean).join(' · ')}
              </Type>
              <StatusText $color={cfg.color}>
                {t(`common.flowStatus.${status}`, {
                  defaultValue: status === 'approved' ? 'Onaylandı'
                    : status === 'waiting' ? 'Bekliyor'
                    : status === 'rejected' ? 'Reddedildi'
                    : 'Sırada',
                })}
              </StatusText>
            </Content>
          </Item>
        );
      })}
    </Wrapper>
  );
}
